import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import api from '../../services/api';
import { useAppTheme } from '../../hooks/useAppTheme';

/**
 * OffsiteBackupCard — "can I trust my backups?", answered in the app.
 *
 * WHY THIS EXISTS
 * The nightly encrypted off-site backup (infra/backup.sh, 01:15, to Backblaze B2) and the
 * monthly restore drill (infra/drill.sh, the 5th) have both been running and passing. Neither
 * was visible anywhere in the product, so the only way to know whether last night's backup
 * worked was to SSH to the box and tail a log.
 *
 * THE STATE THAT MATTERS MOST HAS NO EVIDENCE BEHIND IT.
 * A FAILED run writes a log line and sends Telegram. A run that never happened — cron line
 * removed, box down, disk full before the script started — produces nothing at all. So this
 * card leads with AGE, not with a success flag: past ~26h with no completed run, it goes red
 * and says so. That is the only way a silently-stopped backup ever surfaces.
 *
 * THE DRILL LINE IS THE POINT.
 * A backup nobody has restored is a hope. drill.sh actually restores the newest archive into
 * a scratch database and diffs its schema against production; that verdict is the single most
 * valuable fact here, and nothing surfaced it before.
 */

interface DrillInfo {
  state: 'ok' | 'stale' | 'failed' | 'never-ran';
  ok: boolean | null;
  finishedAt: string | null;
  ageMs: number | null;
  archive: string | null;
  failures: string | null;
}

interface BackupInfo {
  ok: boolean;
  stamp: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  ageMs: number | null;
  dbBytes: number;
  kitBytes: number;
  archive: string | null;
  sourceShipped: boolean;
  keepLocalDays: number | null;
  error: string | null;
}

interface Status {
  state: 'ok' | 'stale' | 'failed' | 'never-ran';
  staleAfterHours: number;
  backup: BackupInfo | null;
  drill: DrillInfo;
}

/** "3h ago" / "2d ago" — the shape a human reads without doing subtraction. */
function ageLabel(ms: number | null): string {
  if (ms == null || !Number.isFinite(ms)) return 'unknown';
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)} days ago`;
}

function bytesLabel(n: number): string {
  if (!n) return '—';
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(2)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024).toFixed(0)} KB`;
}

function whenLabel(iso: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  return new Date(t).toLocaleString('en-IN');
}

const OffsiteBackupCard: React.FC = () => {
  const { C } = useAppTheme();
  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get<{ success: boolean; data: Status }>('/system/backup-status');
      setStatus(r?.data?.data || null);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Could not read the backup status.');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const state = status?.state ?? 'never-ran';
  const tone =
    state === 'ok' ? { color: C.bull, icon: <CheckCircleIcon sx={{ fontSize: 16 }} />, label: 'HEALTHY' }
    : state === 'stale' ? { color: C.warn, icon: <WarningAmberIcon sx={{ fontSize: 16 }} />, label: 'OVERDUE' }
    : state === 'failed' ? { color: C.bear, icon: <ErrorOutlineIcon sx={{ fontSize: 16 }} />, label: 'FAILED' }
    : { color: C.dim, icon: <HelpOutlineIcon sx={{ fontSize: 16 }} />, label: 'NO RECORD' };

  const headline =
    state === 'ok' ? `Last backup completed ${ageLabel(status?.backup?.ageMs ?? null)}.`
    : state === 'stale' ? `The last completed backup was ${ageLabel(status?.backup?.ageMs ?? null)} — more than ${status?.staleAfterHours ?? 26} hours. A nightly run has been missed.`
    : state === 'failed' ? `The last run FAILED${status?.backup?.error ? `: ${status.backup.error}` : '.'}`
    : 'No backup has reported in. Either none has run since this was deployed, or the schedule is not running at all.';

  const drill = status?.drill;
  const drillTone =
    drill?.state === 'ok' ? C.bull
    : drill?.state === 'failed' ? C.bear
    : drill?.state === 'stale' ? C.warn
    : C.dim;
  const drillText =
    drill?.state === 'ok' ? `PASSED ${ageLabel(drill.ageMs)} — the archive restored to a database identical to production.`
    : drill?.state === 'failed' ? `FAILED${drill.failures ? `: ${drill.failures}` : '.'}`
    : drill?.state === 'stale' ? `Last passed ${ageLabel(drill.ageMs)} — a monthly cycle has been missed.`
    : 'No drill has reported in yet. It runs on the 5th of each month.';

  const Row: React.FC<{ label: string; children: React.ReactNode; tip?: string }> = ({ label, children, tip }) => (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', py: 0.35 }}>
      <Tooltip title={tip || ''} arrow disableHoverListener={!tip}>
        <Typography variant="caption" sx={{
          minWidth: 132, color: 'text.secondary', fontWeight: 600,
          cursor: tip ? 'help' : 'default',
        }}>{label}</Typography>
      </Tooltip>
      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{children}</Typography>
    </Box>
  );

  return (
    <Card sx={{ borderLeft: `4px solid ${tone.color}` }}>
      <CardHeader
        title="Off-site Backup"
        subheader="The automatic nightly copy of the whole database, encrypted and stored off this server"
        action={
          <Button size="small" startIcon={loading ? <CircularProgress size={12} /> : <RefreshIcon />}
            onClick={() => void load()} disabled={loading}>
            Refresh
          </Button>
        }
      />
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Chip
            icon={tone.icon}
            label={tone.label}
            size="small"
            sx={{
              fontWeight: 800, letterSpacing: 0.4,
              color: tone.color, bgcolor: `${tone.color}22`,
              border: `1px solid ${tone.color}66`,
              '& .MuiChip-icon': { color: tone.color },
            }}
          />
          <Typography variant="body2" sx={{ color: state === 'ok' ? 'text.secondary' : tone.color, fontWeight: state === 'ok' ? 400 : 600 }}>
            {error || headline}
          </Typography>
        </Box>

        {status?.backup && (
          <Box sx={{ mb: 1.5 }}>
            {/* "Completed" is the wrong word for a run that did not complete. */}
            <Row
              label={status.backup.ok ? 'Completed' : 'Last attempt'}
              tip="Shown in YOUR local time. The schedule below is quoted in server time (Europe/Berlin), so the two read a few hours apart — that is the timezone gap, not a discrepancy."
            >
              {whenLabel(status.backup.finishedAt)}
            </Row>
            <Row label="Database archive" tip="Encrypted, compressed dump of the database. A size that suddenly SHRINKS is a broken backup, so this is worth glancing at.">
              {bytesLabel(status.backup.dbBytes)}
              {status.backup.archive ? <Box component="span" sx={{ color: 'text.disabled', ml: 1 }}>{status.backup.archive}</Box> : null}
            </Row>
            <Row label="Rebuild kit" tip="Your .env files, nginx config and TLS certificates. Small, but without it a restored database is a dead app — the broker credentials cannot be decrypted.">
              {bytesLabel(status.backup.kitBytes)}
              {status.backup.sourceShipped ? <Box component="span" sx={{ color: 'text.disabled', ml: 1 }}>+ source bundle</Box> : null}
            </Row>
            {status.backup.keepLocalDays != null && (
              <Row label="Kept on server" tip="Local copies are the fast path for an 'oops' restore. The off-site copies on Backblaze are kept far longer.">
                {status.backup.keepLocalDays} days
              </Row>
            )}
          </Box>
        )}

        <Box sx={{
          p: 1.25, borderRadius: 1,
          border: `1px solid ${drillTone}55`, bgcolor: `${drillTone}11`,
        }}>
          <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.4, color: drillTone, display: 'block' }}>
            MONTHLY RESTORE DRILL
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.25 }}>{drillText}</Typography>
          {drill?.finishedAt && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {whenLabel(drill.finishedAt)}{drill.archive ? ` · ${drill.archive}` : ''}
            </Typography>
          )}
        </Box>

        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.5 }}>
          Runs automatically every night at 01:15 server time (Europe/Berlin) and uploads an
          encrypted copy to Backblaze B2; times above are shown in your own local time. This is
          <b> separate from</b> the “Download Backup Now” button above, which exports your own
          trades and alerts as a JSON file you keep yourself. Restoring from the off-site copy is
          documented in <code>docs/RESTORE.md</code>.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default OffsiteBackupCard;
